import { Head } from "@inertiajs/react"
import { useState, useEffect, useCallback } from "react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount } from "@/components/ui/avatar"
import { Skeleton } from "@/components/ui/skeleton"
import { Loader } from "@/components/ui/loader"
import { Separator } from "@/components/ui/separator"
import { RoleBadge } from "@/components/role-badge"
import { StatusIndicator } from "@/components/status-indicator"
import { UserDisplay } from "@/components/user-display"
import { Pagination } from "@/components/pagination"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import {
  Plus,
  Settings,
  Trash2,
  Search,
  Download,
  AlertCircle,
  Info,
  ChevronDown,
  MoreHorizontal,
  Mail,
  User,
  Bell,
  Copy,
  Check,
  Sun,
  Moon,
  ArrowUpDown,
} from "lucide-react"

// ---------------------------------------------------------------------------
// Section definitions with nav categories
// ---------------------------------------------------------------------------

const navCategories = [
  {
    label: "Fondations",
    items: [
      { id: "couleurs", label: "Couleurs" },
      { id: "typographie", label: "Typographie" },
    ],
  },
  {
    label: "Composants",
    items: [
      { id: "boutons", label: "Boutons" },
      { id: "formulaires", label: "Formulaires" },
      { id: "badges", label: "Badges" },
      { id: "cartes", label: "Cartes" },
      { id: "tableaux", label: "Tableaux" },
    ],
  },
  {
    label: "Overlays",
    items: [
      { id: "dialogues", label: "Dialogues" },
      { id: "alertes", label: "Alertes" },
    ],
  },
  {
    label: "Navigation & Retour",
    items: [
      { id: "navigation", label: "Navigation" },
      { id: "retour", label: "Retour utilisateur" },
      { id: "avatars", label: "Avatars" },
      { id: "pagination", label: "Pagination" },
    ],
  },
]

const allSections = navCategories.flatMap((c) => c.items)

// ---------------------------------------------------------------------------
// Color swatches — grouped semantically
// ---------------------------------------------------------------------------

const colorGroups = [
  {
    label: "Marque",
    colors: [
      { name: "primary", css: "--primary", tw: "bg-primary", fg: "text-primary-foreground", hex: "#1e40af" },
      { name: "secondary", css: "--secondary", tw: "bg-secondary", fg: "text-secondary-foreground", hex: "#46a097" },
      { name: "accent", css: "--accent", tw: "bg-accent", fg: "text-accent-foreground", hex: "#fa814c" },
    ],
  },
  {
    label: "Semantique",
    colors: [
      { name: "destructive", css: "--destructive", tw: "bg-destructive", fg: "text-white", hex: "#fb7d5b" },
      { name: "warning", css: "--warning", tw: "bg-warning", fg: "text-foreground", hex: "#fcc43e" },
      { name: "success", css: "--success", tw: "bg-success", fg: "text-white", hex: "#46a097" },
    ],
  },
  {
    label: "Surfaces",
    colors: [
      {
        name: "background",
        css: "--background",
        tw: "bg-background",
        fg: "text-foreground",
        hex: "#f8f9fb",
        border: true,
      },
      { name: "card", css: "--card", tw: "bg-card", fg: "text-card-foreground", hex: "#ffffff", border: true },
      { name: "muted", css: "--muted", tw: "bg-muted", fg: "text-muted-foreground", hex: "#eef0f5", border: true },
      { name: "border", css: "--border", tw: "bg-border", fg: "text-foreground", hex: "#e2e6ef" },
      { name: "foreground", css: "--foreground", tw: "bg-foreground", fg: "text-background", hex: "#1b2559" },
      {
        name: "muted-foreground",
        css: "--muted-foreground",
        tw: "bg-muted-foreground",
        fg: "text-white",
        hex: "#a3aed0",
      },
    ],
  },
  {
    label: "Graphiques",
    colors: [
      { name: "chart-1", css: "--chart-1", tw: "bg-chart-1", fg: "text-white", hex: "#1e40af" },
      { name: "chart-2", css: "--chart-2", tw: "bg-chart-2", fg: "text-white", hex: "#46a097" },
      { name: "chart-3", css: "--chart-3", tw: "bg-chart-3", fg: "text-white", hex: "#fa814c" },
      { name: "chart-4", css: "--chart-4", tw: "bg-chart-4", fg: "text-foreground", hex: "#fcc43e" },
      { name: "chart-5", css: "--chart-5", tw: "bg-chart-5", fg: "text-white", hex: "#1b2559" },
    ],
  },
]

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useActiveSection(sectionIds: string[]) {
  const [activeId, setActiveId] = useState(sectionIds[0])

  useEffect(() => {
    const scrollRoot = document.querySelector("main.flex-1")
    if (!scrollRoot) return

    const elements = sectionIds.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[]

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { root: scrollRoot, rootMargin: "-10% 0px -60% 0px", threshold: 0 },
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [sectionIds])

  return activeId
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  const copy = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }, [value])

  return (
    <button
      onClick={copy}
      className="inline-flex items-center justify-center size-6 rounded-md hover:bg-foreground/10 transition-colors"
      title={`Copier ${value}`}
    >
      {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3 text-muted-foreground" />}
    </button>
  )
}

function SectionWrapper({
  id,
  index,
  title,
  children,
}: {
  id: string
  index: number
  title: string
  children: React.ReactNode
}) {
  const isEven = index % 2 === 0
  const num = String(index + 1).padStart(2, "0")

  return (
    <section className={cn("py-10", isEven && "bg-muted/30 -mx-6 px-6 rounded-2xl")}>
      <div className="flex items-center gap-3 mb-6" id={id}>
        <span className="flex items-center justify-center size-8 rounded-lg bg-primary text-primary-foreground text-xs font-bold shrink-0">
          {num}
        </span>
        <h2 className="text-xl font-semibold tracking-tight scroll-mt-20">{title}</h2>
      </div>
      {children}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Typography staircase data
// ---------------------------------------------------------------------------

const typeSteps = [
  {
    label: "h1",
    tw: "text-4xl font-bold",
    size: "2.25rem / 36px",
    weight: "700",
    sample: "Titre principal — Kaptcher",
  },
  {
    label: "h2",
    tw: "text-3xl font-semibold",
    size: "1.875rem / 30px",
    weight: "600",
    sample: "Sous-titre — Section importante",
  },
  { label: "h3", tw: "text-2xl font-semibold", size: "1.5rem / 24px", weight: "600", sample: "Titre de section" },
  { label: "h4", tw: "text-xl font-semibold", size: "1.25rem / 20px", weight: "600", sample: "Sous-section" },
  {
    label: "body",
    tw: "text-base",
    size: "1rem / 16px",
    weight: "400",
    sample: "Corps de texte — Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
  },
  {
    label: "small",
    tw: "text-sm",
    size: "0.875rem / 14px",
    weight: "400",
    sample: "Informations secondaires et metadonnees.",
  },
  {
    label: "muted",
    tw: "text-sm text-muted-foreground",
    size: "0.875rem / 14px",
    weight: "400",
    sample: "Descriptions et aide contextuelle.",
  },
]

const fontWeights = [
  { label: "Regular", tw: "font-normal", weight: "400" },
  { label: "Medium", tw: "font-medium", weight: "500" },
  { label: "Semibold", tw: "font-semibold", weight: "600" },
  { label: "Bold", tw: "font-bold", weight: "700" },
]

// ---------------------------------------------------------------------------
// Table demo data
// ---------------------------------------------------------------------------

const tableUsers = [
  { first: "Marie", last: "Dupont", email: "marie@example.com", role: "admin" as const, active: true },
  { first: "Pierre", last: "Martin", email: "pierre@example.com", role: "user" as const, active: true },
  { first: "Sophie", last: "Bernard", email: "sophie@example.com", role: "user" as const, active: false },
  { first: "Lucas", last: "Petit", email: "lucas@example.com", role: "admin" as const, active: true },
]

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function UiKit() {
  const activeSection = useActiveSection(allSections.map((s) => s.id))
  const [dark, setDark] = useState(false)
  const [tableSearch, setTableSearch] = useState("")

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark)
    return () => {
      document.documentElement.classList.remove("dark")
    }
  }, [dark])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const filteredUsers = tableUsers.filter(
    (u) => !tableSearch || `${u.first} ${u.last} ${u.email}`.toLowerCase().includes(tableSearch.toLowerCase()),
  )

  let sectionIndex = 0

  return (
    <>
      <Head title="UI Kit" />
      <PageHeader title="UI Kit" />

      {/* Mobile horizontal nav */}
      <nav className="lg:hidden sticky top-0 z-10 bg-background/95 backdrop-blur border-b mb-4 -mt-2 overflow-x-auto">
        <div className="flex gap-1 py-2 min-w-max">
          {allSections.map((s) => (
            <Button
              key={s.id}
              variant="ghost"
              size="xs"
              className={cn(activeSection === s.id && "bg-muted text-foreground font-medium")}
              onClick={() => scrollTo(s.id)}
            >
              {s.label}
            </Button>
          ))}
        </div>
      </nav>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        {/* Desktop sidebar nav */}
        <nav className="hidden lg:block">
          <div className="sticky top-0 space-y-4">
            {/* Dark mode toggle */}
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={() => setDark((d) => !d)}
            >
              {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
              {dark ? "Mode clair" : "Mode sombre"}
            </Button>

            {navCategories.map((cat) => (
              <div key={cat.label}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 px-2">
                  {cat.label}
                </p>
                <div className="space-y-0.5">
                  {cat.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={cn(
                        "block w-full text-left text-sm px-2 py-1.5 rounded-md transition-colors",
                        activeSection === item.id
                          ? "bg-muted text-foreground font-medium border-l-2 border-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                      )}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div>
          {/* Dark mode toggle — mobile */}
          <div className="lg:hidden flex justify-end mb-4">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setDark((d) => !d)}>
              {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
              {dark ? "Mode clair" : "Mode sombre"}
            </Button>
          </div>

          {/* 1. Couleurs */}
          <SectionWrapper id="couleurs" index={sectionIndex++} title="Couleurs">
            <div className="space-y-8">
              {colorGroups.map((group) => (
                <div key={group.label}>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">{group.label}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {group.colors.map((c) => (
                      <div
                        key={c.name}
                        className={cn(
                          "rounded-xl overflow-hidden ring-1 ring-foreground/10",
                          c.border && "ring-foreground/5",
                        )}
                      >
                        <div className={cn("h-20 flex items-center justify-center", c.tw)}>
                          <span className={cn("text-2xl font-bold", c.fg)}>Aa</span>
                        </div>
                        <div className="bg-card p-3 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{c.name}</span>
                            <CopyButton value={c.hex} />
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">{c.hex}</p>
                          <p className="text-xs text-muted-foreground font-mono">var({c.css})</p>
                          <p className="text-xs text-muted-foreground font-mono">{c.tw}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SectionWrapper>

          {/* 2. Typographie */}
          <SectionWrapper id="typographie" index={sectionIndex++} title="Typographie">
            <Card>
              <CardContent className="pt-4">
                <div className="divide-y divide-dashed">
                  {typeSteps.map((step) => (
                    <div
                      key={step.label}
                      className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6 py-4 first:pt-0 last:pb-0"
                    >
                      <div className="md:w-48 shrink-0 space-y-0.5">
                        <p className="text-xs font-mono text-muted-foreground">{step.tw}</p>
                        <p className="text-xs font-mono text-muted-foreground">
                          {step.size} &middot; {step.weight}
                        </p>
                      </div>
                      <p className={step.tw}>{step.sample}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <h3 className="text-sm font-medium text-muted-foreground mb-3 mt-6">Graisses</h3>
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-6">
                  {fontWeights.map((w) => (
                    <div key={w.label} className="space-y-1">
                      <p className={cn("text-2xl", w.tw)}>Kaptcher</p>
                      <p className="text-xs font-mono text-muted-foreground">
                        {w.label} ({w.weight})
                      </p>
                      <p className="text-xs font-mono text-muted-foreground">{w.tw}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <h3 className="text-sm font-medium text-muted-foreground mb-3 mt-6">Lien</h3>
            <a href="#typographie" className="text-sm text-primary underline underline-offset-4 hover:text-primary/80">
              Lien textuel
            </a>
          </SectionWrapper>

          {/* 3. Boutons */}
          <SectionWrapper id="boutons" index={sectionIndex++} title="Boutons">
            <Card>
              <CardHeader>
                <CardTitle>Variantes &times; Tailles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-28">Variante</TableHead>
                        <TableHead>xs</TableHead>
                        <TableHead>sm</TableHead>
                        <TableHead>default</TableHead>
                        <TableHead>lg</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(["default", "secondary", "outline", "ghost", "destructive", "link"] as const).map((variant) => (
                        <TableRow key={variant}>
                          <TableCell className="font-mono text-xs text-muted-foreground">{variant}</TableCell>
                          <TableCell>
                            <Button variant={variant} size="xs">
                              xs
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button variant={variant} size="sm">
                              sm
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button variant={variant}>default</Button>
                          </TableCell>
                          <TableCell>
                            <Button variant={variant} size="lg">
                              lg
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Avec icones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2">
                  <Button>
                    <Plus data-icon="inline-start" /> Ajouter
                  </Button>
                  <Button variant="outline">
                    <Download data-icon="inline-start" /> Exporter
                  </Button>
                  <Button variant="destructive">
                    <Trash2 data-icon="inline-start" /> Supprimer
                  </Button>
                  <Button variant="secondary">
                    <Settings data-icon="inline-start" /> Parametres
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Icones seules</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="icon-xs">
                    <Plus />
                  </Button>
                  <Button variant="outline" size="icon-sm">
                    <Settings />
                  </Button>
                  <Button variant="outline" size="icon">
                    <Search />
                  </Button>
                  <Button variant="outline" size="icon-lg">
                    <Download />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Etats desactives</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2">
                  <Button disabled>Defaut</Button>
                  <Button variant="outline" disabled>
                    Outline
                  </Button>
                  <Button variant="destructive" disabled>
                    Destructive
                  </Button>
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 4. Formulaires */}
          <SectionWrapper id="formulaires" index={sectionIndex++} title="Formulaires">
            <Card>
              <CardHeader>
                <CardTitle>Ajouter un utilisateur</CardTitle>
                <CardDescription>Remplissez les informations pour creer un compte.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="form-prenom">Prenom</Label>
                      <Input id="form-prenom" placeholder="Jean" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="form-nom">Nom</Label>
                      <Input id="form-nom" placeholder="Dupont" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="form-email">Adresse e-mail</Label>
                    <Input id="form-email" type="email" placeholder="jean@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrateur</SelectItem>
                        <SelectItem value="user">Utilisateur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="form-password">Mot de passe</Label>
                      <Input id="form-password" type="password" placeholder="••••••••" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="form-password-confirm">Confirmer</Label>
                      <Input id="form-password-confirm" type="password" placeholder="••••••••" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="form-notif" />
                    <Label htmlFor="form-notif">Activer les notifications</Label>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="outline">Annuler</Button>
                <Button>Creer</Button>
              </CardFooter>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Etats des champs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="disabled-input">Champ desactive</Label>
                    <Input id="disabled-input" disabled value="Non modifiable" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="error-input">Champ en erreur</Label>
                    <Input id="error-input" aria-invalid="true" defaultValue="valeur incorrecte" />
                    <p className="text-sm text-destructive">Ce champ est obligatoire.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 5. Badges */}
          <SectionWrapper id="badges" index={sectionIndex++} title="Badges">
            <Card>
              <CardHeader>
                <CardTitle>Variantes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge>Defaut</Badge>
                  <Badge variant="secondary">Secondaire</Badge>
                  <Badge variant="outline">Outline</Badge>
                  <Badge variant="destructive">Destructif</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Badges de role</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <RoleBadge role="admin" />
                  <RoleBadge role="user" />
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Indicateurs de statut</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <StatusIndicator active={true} />
                  <StatusIndicator active={false} />
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 6. Cartes */}
          <SectionWrapper id="cartes" index={sectionIndex++} title="Cartes">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Titre de la carte</CardTitle>
                  <CardDescription>Description courte de la carte avec du contexte.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">
                    Contenu principal de la carte. Peut inclure du texte, des formulaires ou d'autres composants.
                  </p>
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                  <Button variant="outline">Annuler</Button>
                  <Button>Enregistrer</Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className="text-base">Equipe Marketing</CardTitle>
                    <p className="text-xs text-muted-foreground">3 membres</p>
                  </div>
                  <Button variant="ghost" size="icon-sm">
                    <MoreHorizontal />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    <UserDisplay
                      user={{ first_name: "Marie", last_name: "Dupont", email_address: "marie@example.com" }}
                      showEmail
                    />
                    <UserDisplay
                      user={{ first_name: "Pierre", last_name: "Martin", email_address: "pierre@example.com" }}
                      showEmail
                    />
                    <UserDisplay
                      user={{ first_name: "Sophie", last_name: "Bernard", email_address: "sophie@example.com" }}
                      showEmail
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </SectionWrapper>

          {/* 7. Tableaux */}
          <SectionWrapper id="tableaux" index={sectionIndex++} title="Tableaux">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <CardTitle>Utilisateurs</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher..."
                      className="pl-8"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <span className="inline-flex items-center gap-1">
                            Nom <ArrowUpDown className="size-3 text-muted-foreground" />
                          </span>
                        </TableHead>
                        <TableHead>
                          <span className="inline-flex items-center gap-1">
                            E-mail <ArrowUpDown className="size-3 text-muted-foreground" />
                          </span>
                        </TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((u) => (
                        <TableRow key={u.email}>
                          <TableCell>
                            <UserDisplay user={{ first_name: u.first, last_name: u.last }} size="sm" />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                          <TableCell>
                            <RoleBadge role={u.role} />
                          </TableCell>
                          <TableCell>
                            <StatusIndicator active={u.active} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon-sm">
                                <Settings className="size-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 8. Dialogues */}
          <SectionWrapper id="dialogues" index={sectionIndex++} title="Dialogues">
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">Ouvrir un Dialog</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Titre du dialogue</DialogTitle>
                        <DialogDescription>
                          Description du dialogue. Ajoutez ici des informations complementaires.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogBody className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="dialog-name">Nom</Label>
                          <Input id="dialog-name" placeholder="Entrez un nom" />
                        </div>
                      </DialogBody>
                      <DialogFooter>
                        <Button variant="outline">Annuler</Button>
                        <Button>Confirmer</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive">Ouvrir un AlertDialog</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Etes-vous sur ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action est irreversible. Les donnees seront definitivement supprimees.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction>Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline">Ouvrir un Sheet</Button>
                    </SheetTrigger>
                    <SheetContent>
                      <SheetHeader>
                        <SheetTitle>Panneau lateral</SheetTitle>
                        <SheetDescription>Contenu detaille affiche dans un panneau glissant.</SheetDescription>
                      </SheetHeader>
                      <div className="p-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Nom</Label>
                          <Input placeholder="Entrez un nom" />
                        </div>
                        <div className="space-y-2">
                          <Label>E-mail</Label>
                          <Input type="email" placeholder="nom@example.com" />
                        </div>
                        <Button className="w-full">Enregistrer</Button>
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 9. Alertes */}
          <SectionWrapper id="alertes" index={sectionIndex++} title="Alertes et notifications">
            <Card>
              <CardHeader>
                <CardTitle>Alertes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-w-lg">
                  <Alert>
                    <Info />
                    <AlertTitle>Information</AlertTitle>
                    <AlertDescription>Ceci est une alerte informative par defaut.</AlertDescription>
                  </Alert>
                  <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>Erreur</AlertTitle>
                    <AlertDescription>Une erreur est survenue lors du traitement de votre demande.</AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Toasts (Sonner)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      toast.success("Operation reussie", { description: "Les modifications ont ete enregistrees." })
                    }
                  >
                    Toast succes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => toast.error("Erreur", { description: "Impossible de sauvegarder." })}
                  >
                    Toast erreur
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => toast.info("Information", { description: "Mise a jour disponible." })}
                  >
                    Toast info
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => toast.warning("Attention", { description: "Espace disque presque plein." })}
                  >
                    Toast warning
                  </Button>
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 10. Navigation */}
          <SectionWrapper id="navigation" index={sectionIndex++} title="Navigation">
            <Card>
              <CardHeader>
                <CardTitle>Fil d'Ariane</CardTitle>
              </CardHeader>
              <CardContent>
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink href="/users">Utilisateurs</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>Marie Dupont</BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Onglets</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="general" className="max-w-md">
                  <TabsList>
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="securite">Securite</TabsTrigger>
                    <TabsTrigger value="notifications">Notifications</TabsTrigger>
                  </TabsList>
                  <TabsContent value="general" className="text-sm text-muted-foreground">
                    Parametres generaux du compte.
                  </TabsContent>
                  <TabsContent value="securite" className="text-sm text-muted-foreground">
                    Mot de passe et authentification.
                  </TabsContent>
                  <TabsContent value="notifications" className="text-sm text-muted-foreground">
                    Preferences de notification.
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Menu deroulant</CardTitle>
              </CardHeader>
              <CardContent>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      Actions <ChevronDown data-icon="inline-end" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <User /> Profil
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Mail /> Messages
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Bell /> Notifications
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive">
                      <Trash2 /> Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 11. Retour utilisateur */}
          <SectionWrapper id="retour" index={sectionIndex++} title="Retour utilisateur">
            <Card>
              <CardHeader>
                <CardTitle>Loader</CardTitle>
              </CardHeader>
              <CardContent>
                <Loader />
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Squelettes de chargement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-w-sm">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <div className="flex items-center gap-3">
                    <Skeleton className="size-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-3 w-3/4" />
                    </div>
                  </div>
                  <Skeleton className="h-24 w-full rounded-lg" />
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Tooltip</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Info />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Ceci est un tooltip</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Settings />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Parametres</TooltipContent>
                  </Tooltip>
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 12. Avatars */}
          <SectionWrapper id="avatars" index={sectionIndex++} title="Avatars">
            <Card>
              <CardHeader>
                <CardTitle>Tailles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Avatar size="sm">
                    <AvatarFallback className="bg-blue-500 text-white">SM</AvatarFallback>
                  </Avatar>
                  <Avatar>
                    <AvatarFallback className="bg-green-500 text-white">MD</AvatarFallback>
                  </Avatar>
                  <Avatar size="lg">
                    <AvatarFallback className="bg-purple-500 text-white">LG</AvatarFallback>
                  </Avatar>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Groupe d'avatars</CardTitle>
              </CardHeader>
              <CardContent>
                <AvatarGroup>
                  <Avatar>
                    <AvatarFallback className="bg-blue-500 text-white">MD</AvatarFallback>
                  </Avatar>
                  <Avatar>
                    <AvatarFallback className="bg-green-500 text-white">PL</AvatarFallback>
                  </Avatar>
                  <Avatar>
                    <AvatarFallback className="bg-purple-500 text-white">SB</AvatarFallback>
                  </Avatar>
                  <Avatar>
                    <AvatarFallback className="bg-orange-500 text-white">LP</AvatarFallback>
                  </Avatar>
                  <AvatarGroupCount>+3</AvatarGroupCount>
                </AvatarGroup>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>UserDisplay</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <UserDisplay user={{ first_name: "Marie", last_name: "Dupont" }} size="sm" />
                  <UserDisplay
                    user={{ first_name: "Pierre", last_name: "Martin", email_address: "pierre@example.com" }}
                    showEmail
                  />
                  <UserDisplay user={{ first_name: "Sophie", last_name: "Bernard" }} size="lg" />
                  <UserDisplay user={{ first_name: "Lucas", last_name: "Petit" }} avatarOnly />
                </div>
              </CardContent>
            </Card>
          </SectionWrapper>

          {/* 13. Pagination */}
          <SectionWrapper id="pagination" index={sectionIndex} title="Pagination">
            <Card>
              <CardContent className="pt-4">
                <Pagination
                  pagy={{
                    count: 156,
                    page: 3,
                    items: 20,
                    pages: 8,
                    from: 41,
                    to: 60,
                    prev: 2,
                    next: 4,
                  }}
                  baseUrl="/ui-kit"
                />
              </CardContent>
            </Card>
          </SectionWrapper>

          <Separator className="my-8" />
          <p className="text-xs text-muted-foreground text-center pb-8">Page visible uniquement en developpement.</p>
        </div>
      </div>
    </>
  )
}

UiKit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default UiKit
